import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInsideInput } from "@/components/ui/forms/standardInsideInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { cn } from "@/modules/lib/utils"
import {
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { HUChronoInput } from "@/modules/ui/components/huChronoInput"
import { type SimpleFormik } from "@/typings/formik"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { GetChronoDefaults, MAX_NUMBER_INPUT, MIN_NUMBER_INPUT } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useEffect, useState } from "react"

type ChronoForTimeProps = {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ChronoSettingsForTimeForm({ formik, className, ...props }: ChronoForTimeProps) {
  const [moreThanOneSet, setMoreThanOneSet] = useState(false)

  const getFieldProps = (name: string) => formik.getFieldProps(name as ExerciseInputKey)

  useEffect(() => {
    if (!formik.values.chronoForTime) {
      void formik.setFieldValue("chronoForTime", GetChronoDefaults(ChronoType.FOR_TIME))
    }
  }, [])

  useEffect(() => {
    if (formik.values.chronoForTime?.numberOfSets && formik.values.chronoForTime.numberOfSets > 1) {
      setMoreThanOneSet(true)
    }
  }, [formik.values.chronoForTime])

  const addSet = () => {
    if (formik.values.chronoForTime) {
      setMoreThanOneSet(true)
      void formik.setFieldValue("chronoForTime.numberOfSets", formik.values.chronoForTime.numberOfSets + 1)
    }
  }

  const timeOptions = [
    { value: "true", label: word("chrono.settings.for_time.options.no_limit") },
    { value: "false", label: word("chrono.settings.for_time.options.with_limit") },
  ]

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <DropdownInput
        label={word("chrono.settings.for_time.label.no_limit")}
        options={timeOptions}
        error={getFormErrorMessage("chronoForTime.noLimit", formik)}
      >
        <Select
          defaultValue={formik.values.chronoForTime?.noLimit ? "false" : "true"}
          onValueChange={(value) => {
            void formik.setFieldValue("chronoForTime.noLimit", value === "true" ? true : false)
            if (value === "true") {
              void formik.setFieldValue("chronoForTime.trainingTime", 0)
              void formik.setFieldValue("chronoForTime.numberOfSets", 1)
              void formik.setFieldValue("chronoForTime.restTime", 0)
            }
          }}
          items={timeOptions}
        >
          <SelectTrigger id={"noLimit"} className="bg-surface-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DropdownInput>

      {!formik.values.chronoForTime?.noLimit && (
        <>
          <HUChronoInput
            name="chronoForTime.trainingTime"
            label={word("chrono.settings.label.training_time")}
            getFieldProps={getFieldProps}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("chronoForTime.trainingTime", formik, { showAlways: true })}
          />

          {moreThanOneSet && (
            <>
              <StandardInsideInput
                label={word("chrono.settings.label.number_sets")}
                name="chronoForTime.numberOfSets"
                error={getFormErrorMessage("chronoForTime.numberOfSets", formik)}
                getFieldProps={getFieldProps}
                setFieldValue={formik.setFieldValue}
                inputProps={{
                  type: "number",
                  max: MAX_NUMBER_INPUT,
                  min: MIN_NUMBER_INPUT,
                  onChange: (e) => {
                    const value = parseInt(e.target.value)
                    if (value > MAX_NUMBER_INPUT)
                      void formik.setFieldValue("chronoForTime.numberOfSets", MAX_NUMBER_INPUT)
                    else void formik.setFieldValue("chronoForTime.numberOfSets", value)
                  },
                }}
              />

              <HUChronoInput
                name="chronoForTime.restTime"
                label={word("chrono.settings.label.rest_between_sets")}
                getFieldProps={getFieldProps}
                setFieldValue={formik.setFieldValue}
                error={getFormErrorMessage("chronoForTime.restTime", formik, { showAlways: true })}
              />
            </>
          )}
          {!moreThanOneSet && (
            <Button variant="outline" onClick={addSet}>
              <PlusIcon color="currentColor" className="text-textDefault-body" />
              {word("chrono.settings.label.add_set")}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
